/**
 * Cookie consent state — single source of truth for whether the user
 * has accepted, rejected, or not yet decided about non-essential
 * cookies, plus the per-category preferences (functional / analytics /
 * marketing) that downstream consumers gate behavior on.
 *
 * Tri-state {@link consent} is preserved for the existing `gtag`
 * gating in {@link ../app.component.ts}: it returns `null` until the
 * user makes a decision, `'accepted'` if any non-essential category is
 * on, `'rejected'` if all non-essential categories are off. Granular
 * consumers read {@link preferences} to drive specific scripts (e.g.
 * marketing pixels separately from analytics).
 *
 * Persisted to `localStorage` under {@link STORAGE_KEY} as a JSON
 * envelope including `version` (bumped when the cookie policy
 * materially changes — forces re-consent) and `decidedAt` (ISO
 * timestamp, useful for audit logs and right-to-be-forgotten requests).
 *
 * Regression-locked by {@link ./cookie-consent.service.spec.ts}.
 */
import { Injectable, signal, computed, type Signal } from '@angular/core';
import { safeGetItem, safeSetItem, safeRemoveItem } from './safe-storage';

const STORAGE_KEY = 'cookie-consent';

/**
 * Bumped whenever the cookie policy materially changes (new third-party
 * cookie, changed retention, changed categories). Older stored consent
 * is treated as undecided so the banner re-prompts.
 */
export const COOKIE_POLICY_VERSION = 2;

/** Tri-state consent value exposed to legacy `gtag` gating. */
export type CookieConsent = 'accepted' | 'rejected' | null;

/**
 * Per-category preferences. `necessary` is always `true` (strictly
 * necessary cookies do not require consent under GDPR/ePrivacy).
 */
export interface CookiePreferences {
  necessary: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface PersistedConsent {
  version: number;
  decidedAt: string;
  preferences: CookiePreferences;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly stored = signal<PersistedConsent | null>(this.read());

  /**
   * Tri-state view for the existing gtag gating. `null` until the user
   * decides; `'accepted'` if any non-essential category is enabled,
   * `'rejected'` if every non-essential category is off.
   */
  readonly consent: Signal<CookieConsent> = computed(() => {
    const s = this.stored();
    if (!s) return null;
    const { functional, analytics, marketing } = s.preferences;
    return functional || analytics || marketing ? 'accepted' : 'rejected';
  });

  /**
   * Granular preferences signal. Defaults (all non-essential off) are
   * returned when the user has not yet decided, so granular consumers
   * fail closed.
   */
  readonly preferences: Signal<CookiePreferences> = computed(
    () => this.stored()?.preferences ?? DEFAULT_PREFERENCES,
  );

  /**
   * True iff the user has made an explicit choice for the current
   * policy version. Drives banner visibility.
   */
  readonly hasDecided: Signal<boolean> = computed(() => this.stored() !== null);

  /** Accept every non-essential category. */
  acceptAll(): void {
    this.persist({ necessary: true, functional: true, analytics: true, marketing: true });
  }

  /** Reject every non-essential category (necessary stays on). */
  rejectAll(): void {
    this.persist({ ...DEFAULT_PREFERENCES });
  }

  /** Persist a custom selection from the preferences panel. */
  savePreferences(prefs: Omit<CookiePreferences, 'necessary'>): void {
    this.persist({ necessary: true, ...prefs });
  }

  /**
   * Clear the persisted decision so the banner re-prompts. Used by the
   * footer "Cookie Preferences" entry-point so the user can change
   * their mind without having to clear browser storage manually.
   */
  reopen(): void {
    // Storage may be unavailable — safeRemoveItem also clears the
    // in-memory fallback, and resetting the signal handles this session.
    safeRemoveItem('local', STORAGE_KEY);
    this.stored.set(null);
  }

  /** Legacy alias for {@link acceptAll} — used by older tests / callers. */
  accept(): void {
    this.acceptAll();
  }

  /** Legacy alias for {@link rejectAll} — used by older tests / callers. */
  reject(): void {
    this.rejectAll();
  }

  /** True iff the user opted in to analytics specifically. */
  hasAccepted(): boolean {
    return this.preferences().analytics;
  }

  private persist(prefs: CookiePreferences): void {
    const record: PersistedConsent = {
      version: COOKIE_POLICY_VERSION,
      decidedAt: new Date().toISOString(),
      preferences: prefs,
    };
    this.write(record);
    this.stored.set(record);
  }

  private read(): PersistedConsent | null {
    try {
      const raw = safeGetItem('local', STORAGE_KEY);
      if (!raw) return null;
      return this.hydrateLegacy(raw) ?? this.hydrateRecord(raw);
    } catch {
      // Defensive: JSON.parse in hydrateRecord can still throw on
      // corrupt data even though storage access itself is now safe.
      return null;
    }
  }

  /**
   * Tri-state strings from the pre-granular implementation. Treated as
   * a coarse accept/reject so users who already decided don't get
   * re-prompted unnecessarily after the upgrade.
   */
  private hydrateLegacy(raw: string): PersistedConsent | null {
    if (raw !== 'accepted' && raw !== 'rejected') return null;
    return {
      version: COOKIE_POLICY_VERSION,
      decidedAt: new Date(0).toISOString(),
      preferences:
        raw === 'accepted'
          ? { necessary: true, functional: true, analytics: true, marketing: true }
          : { ...DEFAULT_PREFERENCES },
    };
  }

  private hydrateRecord(raw: string): PersistedConsent | null {
    const parsed = JSON.parse(raw) as Partial<PersistedConsent>;
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      parsed.version !== COOKIE_POLICY_VERSION ||
      typeof parsed.preferences !== 'object'
    ) {
      return null;
    }
    const p = parsed.preferences as Partial<CookiePreferences>;
    return {
      version: COOKIE_POLICY_VERSION,
      decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : new Date().toISOString(),
      preferences: {
        necessary: true,
        functional: Boolean(p.functional),
        analytics: Boolean(p.analytics),
        marketing: Boolean(p.marketing),
      },
    };
  }

  private write(record: PersistedConsent): void {
    // Storage may be unavailable (private mode / blocked) — safeSetItem
    // keeps an in-memory copy and the signal carries this session.
    safeSetItem('local', STORAGE_KEY, JSON.stringify(record));
  }
}
